import Image from "next/image";
import { Logo4, Award, Shield_tick, flash_circle, profile_user } from "@/components/icons/assets";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const missionValues = [
  {
    icon: Award,
    title: "GIVING WHEELS A SECOND LIFE",
    description: "We promote reuse and refurbishment to reduce waste and extend wheel life.",
    iconBg: "",
  },
  {
    icon: Shield_tick,
    title: "TRUST COMES FIRST",
    description: "Quality-checked wheels, clear listings, and secure payments you can trust.",
    iconBg: "",
  },
  {
    icon: flash_circle,
    title: "BUILT FOR REAL DRIVERS",
    description: "Simple tools that help drivers buy and sell wheels with confidence.",
    iconBg: "",
  },
  {
    icon: profile_user,
    title: "POWERED BY COMMUNITY",
    description: "A marketplace that connects buyers and sellers through fair pricing.",
    iconBg: "",
  },
];

export default function OurStorySection() {
  return (
    <section className="py-10 sm:py-14 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 lg:px-20 bg-white">
      {/* Header */}
      <div className="text-center mb-8 sm:mb-12 md:mb-16">
        <Badge variant="outline" className="font-plus-jakarta text-xs sm:text-sm font-medium text-neutral-500 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-6">
          Our Story
        </Badge>
        <h2 className="font-plus-jakarta text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4 sm:mb-6">
          BUILT TO SIMPLIFY THE WAY<br className="hidden sm:block" /> WHEELS ARE BOUGHT AND SOLD
        </h2>
        <p className="font-plus-jakarta text-xs sm:text-sm text-neutral-300 max-w-4xl mx-auto">
          WheelTox was created to remove the complexity from buying and selling wheels. From finding the right fit to giving used wheels a second life, we built a modern platform that puts clarity, trust, and ease first for every driver.
        </p>
      </div>

      {/* Stats & CEO Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12 md:mb-16">
        {/* Left - Stats Card */}
        <Card className="bg-background-50 border-none rounded-xl sm:rounded-2xl shadow-none">
          <CardContent className="p-4 sm:p-6 md:p-8 h-full flex flex-col justify-between">
            <div>
              <h3 className="font-plus-jakarta text-lg sm:text-xl md:text-2xl font-bold text-neutral-900 mb-2 sm:mb-4">
                TRUSTED BY THOUSANDS OF WHEEL TRANSACTIONS
              </h3>
              <p className="font-plus-jakarta text-sm text-neutral-300 mb-6">
                Every number represents a real driver, a real car, and a wheel that found the right home.
              </p>
            </div>
            
            <Card className="border-none shadow-none rounded-xl sm:rounded-2xl bg-white">
              <CardContent className="p-0">
                {/* Logo */}
                <div className="pt-6">
                  <Logo4 />
                </div>
                
                {/* Horizontal divider */}
                <div className="border-t border-background-50" />
                
                {/* Stats */}
                <div className="flex flex-col sm:flex-row">
                  <div className="flex-1 p-4 sm:p-6 lg:p-4 xl:p-6 whitespace-nowrap">
                    <span className="font-plus-jakarta text-lg sm:text-xl lg:text-base xl:text-xl font-bold text-neutral-900">5,000+</span>
                    <span className="font-plus-jakarta text-lg sm:text-xl lg:text-base xl:text-xl text-neutral-300 font-medium ml-2">Wheels Sold</span>
                  </div>
                  <div className="border-t sm:border-t-0 sm:border-l border-background-50" />
                  <div className="flex-1 p-4 sm:p-6 lg:p-4 xl:p-6 whitespace-nowrap">
                    <span className="font-plus-jakarta text-lg sm:text-xl lg:text-base xl:text-xl font-bold text-neutral-900">3,000+</span>
                    <span className="font-plus-jakarta text-lg sm:text-xl lg:text-base xl:text-xl text-neutral-300 font-medium ml-2">Wheels Bought</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>

        {/* Right - CEO Image */}
        <div className="relative rounded-xl sm:rounded-2xl overflow-hidden min-h-[400px] md:min-h-[800px]">
          <Image
            src="/image/about/ceo.png"
            alt="CEO"
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
            className="object-cover"
          />
          <span className="absolute top-4 sm:top-8 left-4 sm:left-8 font-plus-jakarta text-lg sm:text-xl font-bold text-neutral-900">
            MEET CEO
          </span>
        </div>
      </div>

      {/* Mission & Values */}
      <Card className="bg-background-50 border-none rounded-xl sm:rounded-2xl shadow-none">
        <CardContent className="p-4 sm:p-6 md:p-10">
          <h3 className="font-plus-jakarta text-lg sm:text-xl md:text-2xl font-bold text-neutral-900 mb-4 sm:mb-6 md:mb-8">
            OUR MISSION & VALUES
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {missionValues.map((item, index) => (
              <Card key={index} className="bg-white border-none rounded-lg sm:rounded-xl shadow-none">
                <CardContent className="p-4 sm:p-6 md:p-10">
                  <item.icon className="mb-4 sm:mb-6 md:mb-10" />
                  <h4 className="font-plus-jakarta text-base sm:text-lg md:text-xl font-bold text-neutral-900 mb-2">
                    {item.title}
                  </h4>
                  <p className="font-plus-jakarta text-neutral-300 text-xs sm:text-sm font-regular max-w-[400px]">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
